import type { AnalyticsEvent } from '../types';

const EVENTS_KEY = 'oemstream_analytics_event_cache';
const SYNC_KEY = 'oemstream_analytics_last_sync';
const VERSION_KEY = 'oemstream_analytics_cache_version';

// Bump when the stored event shape changes (e.g. newly retained properties).
// A mismatch discards the cache and forces a full backfill on next sync.
const CACHE_SCHEMA = 2;

// Keep the local store bounded; localStorage is ~5 MB. Raw tracked events
// (custom commerce events + pageviews only) are small, so 2,000 is generous.
const MAX_CACHED_EVENTS = 2000;

// System ($-prefixed) properties worth retaining for filtering and display:
// geo/location, network, device, and session context. Everything else
// ($viewport, $lib details, feature flags, …) is dropped to save space.
const KEEP_SYSTEM_PROPS = new Set([
  '$ip',
  '$geoip_country_name',
  '$geoip_country_code',
  '$geoip_city',
  '$geoip_region',
  '$geoip_postal_code',
  '$geoip_time_zone',
  '$browser',
  '$browser_version',
  '$os',
  '$device_type',
  '$session_id',
  '$host',
  '$pathname',
  '$current_url',
  '$referring_domain',
]);

// When re-syncing, re-fetch a small overlap window before the last sync
// timestamp so late-arriving events are not missed.
const OVERLAP_MS = 10 * 60 * 1000; // 10 minutes

interface CachePayload {
  events: AnalyticsEvent[];
}

function safeParse<T>(raw: string | null, fallback: T): T {
  if (!raw) return fallback;
  try {
    return JSON.parse(raw) as T;
  } catch {
    return fallback;
  }
}

class AnalyticsCache {
  /** Load every locally stored event (instant, no network). */
  loadEvents(): AnalyticsEvent[] {
    // Stale schema? Discard and let the next sync backfill with the new shape.
    if (localStorage.getItem(VERSION_KEY) !== String(CACHE_SCHEMA)) {
      if (localStorage.getItem(EVENTS_KEY)) this.clear();
      localStorage.setItem(VERSION_KEY, String(CACHE_SCHEMA));
      return [];
    }
    const payload = safeParse<CachePayload>(localStorage.getItem(EVENTS_KEY), { events: [] });
    return payload.events || [];
  }

  /** ISO timestamp of the last successful sync, or null if never synced. */
  getLastSync(): string | null {
    return localStorage.getItem(SYNC_KEY);
  }

  /**
   * The `after` cursor for the next incremental fetch: last sync minus a
   * small overlap so we never skip late arrivals.
   */
  getSyncCursor(): string | null {
    const last = this.getLastSync();
    if (!last) return null;
    return new Date(new Date(last).getTime() - OVERLAP_MS).toISOString();
  }

  /** True when we have never completed a sync (need a full backfill). */
  isCold(): boolean {
    return this.getLastSync() === null;
  }

  /**
   * Merge freshly fetched events into the local store, dedupe by event id,
   * keep the store bounded, and record the sync timestamp.
   */
  mergeNew(fresh: AnalyticsEvent[], syncedAt: Date = new Date()): AnalyticsEvent[] {
    const existing = this.loadEvents();
    const seen = new Map<string, AnalyticsEvent>();

    for (const e of existing) seen.set(e.id, e);
    for (const e of fresh) seen.set(e.id, this.trimEvent(e));

    let merged = [...seen.values()].sort((a, b) => a.timestamp.localeCompare(b.timestamp));

    // Bound the store: drop oldest events beyond the cap.
    if (merged.length > MAX_CACHED_EVENTS) {
      merged = merged.slice(merged.length - MAX_CACHED_EVENTS);
    }

    this.persist(merged);
    localStorage.setItem(SYNC_KEY, syncedAt.toISOString());
    return merged;
  }

  markSynced(syncedAt: Date = new Date()): void {
    localStorage.setItem(SYNC_KEY, syncedAt.toISOString());
  }

  clear(): void {
    localStorage.removeItem(EVENTS_KEY);
    localStorage.removeItem(SYNC_KEY);
  }

  /**
   * Drop noisy $-prefixed system properties (viewport, lib details, feature
   * flags, …) before persisting, while retaining geo/IP/device/session
   * context used for filtering and the raw-event viewer.
   */
  private trimEvent(event: AnalyticsEvent): AnalyticsEvent {
    const props = event.properties || {};
    const trimmed: Record<string, any> = {};
    for (const [key, value] of Object.entries(props)) {
      if (!key.startsWith('$') || KEEP_SYSTEM_PROPS.has(key)) trimmed[key] = value;
    }
    return { ...event, properties: trimmed };
  }

  private persist(events: AnalyticsEvent[]): void {
    const payload: CachePayload = { events };
    try {
      localStorage.setItem(EVENTS_KEY, JSON.stringify(payload));
    } catch {
      // Quota exceeded: shed the oldest half and retry once.
      try {
        localStorage.setItem(EVENTS_KEY, JSON.stringify({ events: events.slice(Math.floor(events.length / 2)) }));
      } catch {
        // Give up silently; next sync will rebuild.
      }
    }
  }
}

export const analyticsCache = new AnalyticsCache();
export default AnalyticsCache;
